// Copyright 2025, University of Maryland, All Rights Reserved
import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import MDEditor from '@uiw/react-md-editor'
import { Button, Label, Switch } from '@/components/ui'
import { useSiteBannerApi } from '../services/banner-api'
import { SITE_BANNER_QUERY_KEY } from '../queryKeys'

export function SiteBannerAdmin() {
  const api = useSiteBannerApi()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: SITE_BANNER_QUERY_KEY,
    queryFn: api.getBanner,
  })

  const [isEnabled, setIsEnabled] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (data) {
      setIsEnabled(data.isEnabled)
      setMessage(data.message)
    }
  }, [data])

  const mutation = useMutation({
    mutationFn: () => api.updateBanner({ isEnabled, message }),
    onSuccess: (updated) => {
      queryClient.setQueryData(SITE_BANNER_QUERY_KEY, updated)
      toast.success('Site banner saved')
    },
    onError: (err: Error) => {
      toast.error(`Failed to save banner: ${err.message}`)
    },
  })

  const dirty = !!data && (data.isEnabled !== isEnabled || data.message !== message)

  return (
    <div className="overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
      <div className="space-y-6 px-6 py-5">
        <div className="flex items-center gap-3">
          <Switch
            id="site-banner-enabled"
            checked={isEnabled}
            onCheckedChange={setIsEnabled}
            disabled={isLoading || mutation.isPending}
          />
          <Label htmlFor="site-banner-enabled">Banner enabled</Label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="site-banner-message">Message</Label>
          <div
            data-color-mode="light"
            className={isLoading || mutation.isPending ? 'pointer-events-none opacity-60' : ''}
          >
            <MDEditor
              value={message}
              onChange={(v) => setMessage(v ?? '')}
              height={200}
              preview="edit"
              textareaProps={{
                placeholder:
                  'e.g. A new release will be deployed at 5pm ET — expect ~2 minutes of downtime.',
              }}
            />
          </div>
        </div>

        {data?.updatedAt && (
          <p className="text-xs text-slate-400">
            Last updated {new Date(data.updatedAt).toLocaleString()}
            {data.updatedBy ? ` by ${data.updatedBy}` : ''}
          </p>
        )}
      </div>
      <div className="flex justify-end bg-[#fafbfc] px-6 py-4">
        <Button
          onClick={() => mutation.mutate()}
          disabled={!dirty || isLoading || mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  )
}
